import {
	buildSiteNavigationState,
	isSiteNavigationReady,
} from './navigation';
import {serializeSiteContext} from './context';
import {
	applySiteFilter,
	focusSite,
	openSiteBulkSection,
	openSiteSection,
} from './adapter';
import {
	openSiteCloneModal,
	openSiteConfigureModal,
	openSiteRadioModal,
	openSiteCreateModal,
	openSiteDeleteModal,
	openSiteEditModal,
	openSiteImportConfigurationModal,
} from './modal';
import {resolveCountries, resolveSites} from './resolver';
import {mapSiteEditPrefill, mapSitePrefill} from './prefill';
import {
	highlightFirstExistingControl,
	revealExistingSection,
} from '../../core/actionHighlighter';
import {readSiteCounts} from './statistics';

const sitePlugin = {
	id: 'site',
	version: 1,
	commandVersion: 1,
	resource: 'site',
	operations: [
		'options',
		'list',
		'search',
		'open',
		'create',
		'edit',
		'configure',
		'radio',
		'delete',
		'clone',
		'import_configuration',
		'export_configuration',
		'download_report',
		'bulk_delete',
		// The bulk addition screen on the Maintenance tab.
		'bulk_add',
		// Opening the Backup section on Maintenance. The section itself is
		// registered by the `backup` plugin, which owns that page - this
		// only declares that a Site request can land there, which the
		// backend catalogue has always said and this list did not.
		//
		// Opening it, not performing a backup: the capability is declared
		// read-only and its commands are navigate and open_section.
		'backup',
	],
	dependencies: {
		// Bulk addition lives on the Maintenance tab, whose destination
		// canvas.navigation provides. Declared so a rename is caught
		// here rather than at the moment someone asks for it.
		destinations: ['maintenance.dashboard'],
	},
	destinations: {
		'site.dashboard': {
			path: '/topology',
			buildState: buildSiteNavigationState,
		},
	},
	completionVerifiers: {
		'navigate:site.dashboard': isSiteNavigationReady,
	},
	resolvers: {
		site: resolveSites,
		country: resolveCountries,
	},
	sections: {
		// Bulk work lives on the Maintenance tab rather than beside the
		// inventory it acts on.
		'maintenance.bulk_add_sites': () =>
			openSiteBulkSection('maintenance.bulk_add_sites'),
		'home.sites': async (command) => {
			const evidence = await openSiteSection('home.sites');
			if (command.params?.action_target === 'bulk_select_delete' &&
				!highlightFirstExistingControl([
					'[role="grid"] [role="checkbox"]',
					'table thead input[type="checkbox"]',
				])) throw new Error('site_bulk_selection_unavailable');
			return evidence;
		},
		// The Sites panel on a Site Dashboard, listing the sites inside this
		// one. Creating a site while standing in a Site belongs here rather
		// than back on the Home Dashboard, which is where it went before this
		// panel had a name the agent could ask for. Failing loudly matters:
		// the agent must not believe it opened something it did not.
		'site.sites': () => {
			if (!revealExistingSection(['Sites'])) {
				throw new Error('site_sites_section_unavailable');
			}
		},
	},
	filters: {
		site: (command) => applySiteFilter(command.params?.query),
	},
	focusHandlers: {
		site: (command) => focusSite(command.params?.entity_id),
	},
	statistics: {
		site_count: readSiteCounts,
	},
	modals: {
		'site.create': openSiteCreateModal,
		'site.edit': openSiteEditModal,
		'site.configure': openSiteConfigureModal,
		'site.radio': openSiteRadioModal,
		'site.delete': openSiteDeleteModal,
		'site.clone': openSiteCloneModal,
		'site.import_configuration': openSiteImportConfigurationModal,
	},
	prefillMappers: {
		'site.create': mapSitePrefill,
		'site.edit': mapSiteEditPrefill,
	},
	contextSerializers: {
		site: serializeSiteContext,
	},
};

export default sitePlugin;
